import React from 'react';

import PartnerCompanyActions from 'scenes/PartnerCompanies/components/Actions';
import { useChildComponent } from '@gogame/hooks-ts';
import { DataGrid } from '@gogame/data-grid';
import { useScreenDispatch, useScreenState } from '@gogame/screens';

import {
  PartnerCompanyFormCreate,
  PartnerCompanyFormEdit,
} from 'scenes/PartnerCompanies/components/PartnerCompanyForm';
import {
  COLUMN_ID,
  COLUMN_NAME,
  COLUMN_STATUS,
  COLUMN_OWNER,
  COLUMN_CREATED_BY,
  COLUMN_COUNTRY,
  COLUMN_UPDATED_DATE,
  COLUMN_CREATED_DATE,
} from 'scenes/PartnerCompanies/constants';
import {
  useLoadPartnerCompanyGrid,
  useIsUpdating,
} from 'scenes/PartnerCompanies/data';
import {
  COLLAPSE_COLUMNS,
  REORDER_COLUMNS,
  SET_TABLE_SETTINGS,
} from 'scenes/PartnerCompanies/screenReducer';
import PartnerCompanyToolbar from './PartnerCompanyToolbar';

import { PartnerCompanyDetailsLink } from './PartnerCompanyDetailsLink';
import { getColor } from 'scenes/PartnerCompanies/components/Status';

const PartnerCompanyDataTable = () => {
  const [childComponent, openChildComponent] = useChildComponent();
  const dispatch = useScreenDispatch();
  const screenState = useScreenState();
  const { filters, tableSettings, searchQuery } = screenState;
  const params = { filters, tableSettings, searchQuery };

  const { data, isLoading, isRefetching } = useLoadPartnerCompanyGrid(params);
  const isUpdating = useIsUpdating();

  const handleCollapse = (collapsed) => {
    dispatch({
      type: COLLAPSE_COLUMNS,
      payload: collapsed,
    });
  };

  const handleChangeSettings = (settings) => {
    dispatch({
      type: SET_TABLE_SETTINGS,
      payload: settings,
    });
  };

  const handleReorder = (order) => {
    dispatch({
      type: REORDER_COLUMNS,
      payload: order,
    });
  };

  const handleEdit = ({ id }) => {
    openChildComponent(PartnerCompanyFormEdit, { id });
  };

  const handleCreate = () => {
    openChildComponent(PartnerCompanyFormCreate);
  };

  return (
    <React.Fragment>
      {childComponent}
      <DataGrid
        rows={data?.rows}
        count={data?.count}
        loading={isLoading || isUpdating}
        fetching={isRefetching}
        onChangeSettings={handleChangeSettings}
        onReorder={handleReorder}
        onCollapse={handleCollapse}
        ready={screenState.ready}
        tableSettings={screenState.tableSettings}
        columnsState={screenState.columns}
        toolbar={<PartnerCompanyToolbar onCreate={handleCreate} />}
        storageKey={'partner-companies'}
        defaultColumnWidth={244}
        filtered={!!searchQuery}
      >
        <DataGrid.Column
          field={COLUMN_ID}
          type={'text'}
          headerName={'ID'}
          align={'right'}
          headerAlign={'right'}
          width={86}
          fixed={true}
          resizable={false}
        />
        <DataGrid.Column
          field={COLUMN_NAME}
          type={'link'}
          headerName={'Name'}
          minWidth={244}
          flex={1}
          linkComponent={PartnerCompanyDetailsLink}
          getTooltip={(v) => v}
        />
        <DataGrid.Column
          field={COLUMN_STATUS}
          headerName={'Status'}
          type={'status'}
          width={110}
          getStatusColor={getColor}
        />
        <DataGrid.Column
          field={COLUMN_COUNTRY}
          type={'text'}
          headerName={'Country'}
        />
        <DataGrid.Column
          field={COLUMN_OWNER}
          type={'text'}
          headerName={'Owner'}
        />
        <DataGrid.Column
          field={COLUMN_CREATED_BY}
          type={'text'}
          headerName={'Created by'}
        />
        <DataGrid.Column
          field={COLUMN_UPDATED_DATE}
          type={'date'}
          template={'LLL'}
          timeZone={'local'}
          placeholder={'-'}
          headerName={'Last Updated'}
        />
        <DataGrid.Column
          field={COLUMN_CREATED_DATE}
          type={'date'}
          template={'LLL'}
          timeZone={'local'}
          placeholder={'-'}
          headerName={'Created'}
        />
        <DataGrid.Column
          actionsColDef
          type={'actions'}
          headerName={'Actions'}
          limit={3}
          onEdit={handleEdit}
          actionsComponent={PartnerCompanyActions}
        />
      </DataGrid>
    </React.Fragment>
  );
};

export default PartnerCompanyDataTable;
